/* eslint-disable no-restricted-globals */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Box, CircularProgress, Fade, Theme, Skeleton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useIntersectionObserver from '../../../lib/hook/useIntersectionObserver';

/**
 * - Using a lower intersection observer visibility threshold because
 *   there could be large documents or pages that overflow the viewport
 * - This value should lower than the DocumentTracker threshold to trigger
 *   the document loading before starting to track
 */
const PDF_VISIBILITY_THRESHOLD = 0.25;

// Using console.time we detected the observer fire events under 100ms for a
// quick scroll. Be aware of this thresholds before changing the timeout
const LOAD_PDF_WITHIN_VIEWPORT_TIMEOUT = 200;

// Timeouts
const FADE_SWAP_TIMEOUT = 500;
const PDFJS_RENDER_DELAY = 1000;
const SHOW_SPINNER_AFTER = 1000;

// Dimensions
// Height of the unsupported document formats (or conversion errors)
const DOC_VIEWER_FALLBACK_HEIGHT = 98;
const DOC_VIEWER_UNKNOWN_HEIGHT = 0;
// @note if there is any zIndex conflicts, keep in mind
//       to check snackbars and remove room items
const LOADER_ZINDEX = 2;
const SPINNER_SIZE = 24;

const useStyles = makeStyles((theme: Theme) => ({
  completed: {},
  restarted: {},
  cover: {
    // Blur added to deal with thumbnails reduced quality
    '-webkit-filter': 'blur(2px)',
    backdropFilter: 'blur(2px)',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    filter: 'blur(2px)',
    opacity: 0.5,
    zIndex: 1,
  },
  fillWidth: {
    maxWidth: '100%',
    width: '100%',
  },
  loader: {
    overflow: 'hidden',
    zIndex: LOADER_ZINDEX,
    '&$restarted': {
      height: '100%',
      left: 0,
      position: 'absolute',
      top: 0,
      width: '100%',
    },
  },
  root: {
    position: 'relative',
    width: '100%',
  },
  rounded: {
    borderRadius: theme.spacing(0.5),
  },
  skeletonWithChildren: {
    '& > *': {
      visibility: 'visible',
    },
  },
  spinner: {
    color: 'var(--document-content-loader-color)',
  },
  spinnerOverlay: {
    background: 'white',
    borderRadius: '50%',
    display: 'flex',
    padding: theme.spacing(1),
    zIndex: 2,
  },
  viewer: {
    // When the document is ready, we pre-render it
    // The document is positioned behind the loader
    bottom: 0,
    position: 'absolute',
    top: 0,
    zIndex: LOADER_ZINDEX - 1,
    '&$completed': {
      // After revealing the document, we remove any height
      // restrictions to let the render be fluid
      bottom: 'auto',
      position: 'static',
      top: 'auto',
      zIndex: 'auto',
    },
  },
  wrapper: {
    alignItems: 'center',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '100%',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
  },
}));

type ClassKey = 'root' | 'viewer';

type Props = {
  allowLoaderRestart?: boolean;
  children: React.ReactNode | ((inViewport: boolean) => React.ReactNode);
  classes?: Partial<Record<ClassKey, string>>;
  cover?: string;
  docHeight?: number;
  docWidth?: number;
  enableViewportDetection?: boolean;
  ready?: boolean;
  rounded?: boolean;
  width?: number | null;
};

/**
 * = Height calculator =
 * This function contains our sizing rules
 * @warn Avoid adding jumps between loading states
 */
const calculateHeight = (
  docHeight: Props['docHeight'],
  docWidth: Props['docWidth'],
  width: Props['width']
) => {
  // The wrapper width must always be known or fallback to hidden
  // SizeMe width initialize undefined until it is detected
  if (Number.isNaN(width!)) {
    return DOC_VIEWER_UNKNOWN_HEIGHT;
  }
  // Keep in mind the document dimensions may not be available
  // If any dimension is undefined, then scale=NaN
  const scale = docHeight! / docWidth!;
  // Calculate the viewer dimensions based on the document scale
  // ...or set the minimum height (unsupported document formats)
  // @warning be aware that `typeof NaN === 'number'`
  return !Number.isNaN(scale) ? width! * scale : DOC_VIEWER_FALLBACK_HEIGHT;
};

/**
 * = PDF Player Loader =
 * This component controls the document loading sequence
 *
 * DEV notes:
 * - Documents support multi-orientation (portrait and landscape)
 * - Fullscreen mode behaves differently
 * - The document viewer is responsive
 * - The children should always be mounted in the DOM to let
 *   the engine load and render the document
 * - There is a "fallback" state for unsupported document formats
 *   (also for conversion errors)
 * - Make sure to thoroughly test any changes.
 *   Any transition should feel smooth
 */
const PDFLoader: React.FC<Props> = (props: Props) => {
  const classes = useStyles();

  const {
    allowLoaderRestart = false,
    children,
    classes: classesProp,
    cover,
    docHeight,
    docWidth,
    enableViewportDetection = false,
    ready = false,
    rounded = false,
    width,
  } = props;

  const knownDocDimensions =
    !Number.isNaN(docHeight!) && !Number.isNaN(docWidth!);

  const [completed, setCompleted] = useState(false);
  // Always start with unknown dimensions
  const [height, setHeight] = useState(DOC_VIEWER_UNKNOWN_HEIGHT);
  // Always load the PDF except:
  // - dimensions are unknown
  // - enable viewport loading optimization
  const [loadPDF, setLoadPDF] = useState(
    !knownDocDimensions || !enableViewportDetection
  );
  const [reveal, setReveal] = useState(false);
  const [showSpinner, setShowSpinner] = useState(true);
  const [inViewport, setInViewport] = useState(!enableViewportDetection);

  const viewer = useRef<HTMLDivElement>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Special state when the loader was restarted back to the not ready state,
   * making the loader to show the spinner and show the blurred cover.
   * This particular state doesn't rely on dimensions, but instead uses a
   * 100% height to avoid messing with the dimensions by showing the cover
   * and the PDF container at the same time.
   */
  const restarted = allowLoaderRestart && completed;

  /**
   * We load the document only if it exists within the viewport for
   * LOAD_PDF_WITHIN_VIEWPORT_TIMEOUT amount of time
   *
   * This performance improvement helps rendering rooms with many documents,
   * by letting the browser handle better its threads and memory
   *
   * @todo do not attach observer if enableViewportDetection=true
   */
  const ref = useIntersectionObserver<HTMLDivElement>((isIntersecting) => {
    // If the document is loading or loaded, avoid any intersector logic
    // @todo deattach observer
    if (!enableViewportDetection || loadPDF) {
      return;
    }
    // Clear the timeout if the document leaves the viewport before running
    if (timeoutRef.current && !isIntersecting) {
      clearTimeout(timeoutRef.current);
      setInViewport(false);
      timeoutRef.current = undefined;
    }
    // Set a timeout when the document is within the viewport
    if (!timeoutRef.current && isIntersecting) {
      timeoutRef.current = setTimeout(() => {
        setLoadPDF(true);
        setInViewport(true);
      }, LOAD_PDF_WITHIN_VIEWPORT_TIMEOUT);
    }
  }, PDF_VISIBILITY_THRESHOLD);

  const recalculateHeight = useCallback(() => {
    setHeight(calculateHeight(docHeight, docWidth, width));
  }, [docHeight, docWidth, width]);

  // Bootstrap loader flow
  useEffect(() => {
    recalculateHeight();
    setTimeout(() => {
      setShowSpinner(true);
    }, SHOW_SPINNER_AFTER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalculate height everytime the doc or viewer dimensions changes
  useEffect(() => {
    if (completed) return;
    recalculateHeight();
  }, [completed, recalculateHeight]);

  /**
   * We control the sequence of elements displayed based on "statuses":
   *
   * Loader   ✓          ✓        ✓          ✓             ✓ (fade out)
   * Document            +        ✓ (memory) ✓ (behind)    ✓ (fade in)  ✓         ✓
   *
   *          |----------|--------|----------|-------------|------------|--------->
   *       Loading   Viewport   Ready   Pre-rendered    Reveal     Completed   Fluid
   *      (default)   500ms               1000ms        500ms
   *
   * Use `enableViewportDetection` to enable the viewport status/phase
   *
   * Fluid is important to support fullscreen and multi-orientation documents
   */
  useEffect(() => {
    if (!ready) {
      if (allowLoaderRestart) {
        setReveal(false);
      }
      return;
    }
    // 1. When the document is ready, we need to pre-render it.
    //    We add a delay to allow the rendering engine work
    setTimeout(() => {
      // 2. Now that the document is ready and rendered behind the loader
      //    we force the known height to always match the final result
      setHeight(viewer.current?.getBoundingClientRect().height ?? height);
      // 3. Start revealing the document and hiding the loader
      //    The timeout must be bigger than FADE_SWAP_TIMEOUT
      setReveal(true);
      setTimeout(() => {
        // 4. Finally! the animation is completed
        //    Release forced measures to let the document be fluid
        setCompleted(true);
      }, FADE_SWAP_TIMEOUT + 100);
    }, PDFJS_RENDER_DELAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const renderSpinner = () =>
    showSpinner && height !== DOC_VIEWER_UNKNOWN_HEIGHT ? (
      <CircularProgress
        classes={{ root: classes.spinner }}
        color="secondary"
        size={SPINNER_SIZE}
      />
    ) : (
      <>&nbsp;</>
    );

  const renderLoaderContent = () => {
    if (cover) {
      // Render cover image with spinner but without pulsing effect
      return (
        <Box className={classes.wrapper}>
          <Box
            className={classNames(classes.cover, classes.wrapper)}
            style={{ backgroundImage: `url(${cover})` }}
          />
          <Box className={classes.spinnerOverlay}>{renderSpinner()}</Box>
        </Box>
      );
    }

    // Fallback to pulsing skeleton with spinner
    return (
      <Skeleton
        classes={{
          root: classes.wrapper,
          withChildren: classes.skeletonWithChildren,
        }}
        variant="rectangular"
      >
        {renderSpinner()}
      </Skeleton>
    );
  };

  return (
    <Box className={classNames(classes.root, classesProp?.root)} ref={ref}>
      <Fade in={!reveal} timeout={FADE_SWAP_TIMEOUT} unmountOnExit={completed}>
        <Box
          className={classNames(classes.loader, {
            [classes.rounded]: rounded,
            [classes.restarted]: restarted,
          })}
          height={restarted ? undefined : height}
          width={restarted ? undefined : width ?? undefined}
        >
          {renderLoaderContent()}
        </Box>
      </Fade>
      {loadPDF && (
        <Fade in={reveal} timeout={FADE_SWAP_TIMEOUT}>
          <Box
            className={classNames(
              classes.fillWidth,
              classes.viewer,
              classesProp?.viewer,
              {
                [classes.completed]: completed,
              }
            )}
            ref={viewer}
          >
            {typeof children === 'function' ? children(inViewport) : children}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default PDFLoader;
